<template>
  <Sidebar class="top-16 h-[calc(100vh-4rem)]">
    <SidebarHeader>
      <div class="pt-4 pl-4">
        <SearchButton />
      </div>
    </SidebarHeader>
    <SidebarContent class="pl-4">
      <SidebarGroup v-for="group in visibleNavigationGroups" :key="group.label">
        <SidebarGroupLabel class="uppercase">{{ group.label }}</SidebarGroupLabel>
        <SidebarMenu>
          <template v-for="item in group.items" :key="item.name">
            <Collapsible v-if="item.children" class="group/collapsible" :default-open="item.isOpen">
              <SidebarMenuItem>
                <CollapsibleTrigger
                  as-child
                  :class="cn([{ 'bg-sidebar-accent': isChildRouteActive(item.children) }])"
                >
                  <SidebarMenuButton :tooltip="item.tooltip" class="[&_svg]:first:size-4.5">
                    <component :is="item.icon" class="shrink-0" />
                    <span>{{ item.name }}</span>
                    <ChevronRightIcon
                      class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem v-for="child in item.children" :key="child.name">
                      <SidebarMenuSubButton
                        as-child
                        class="text-muted-foreground [&.is-active]:bg-primary/5 [&.is-active]:text-primary"
                      >
                        <RouterLink :to="{ name: child.routeName }">
                          {{ child.name }}
                        </RouterLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <SidebarMenuItem v-else>
              <SidebarMenuButton
                as-child
                class="text-muted-foreground [&.is-active]:bg-primary/5 [&.is-active]:text-primary [&_svg]:first:size-4.5"
              >
                <RouterLink v-if="item.routeName" :to="{ name: item.routeName }">
                  <component :is="item.icon" class="shrink-0" />
                  <span>{{ item.name }}</span>
                </RouterLink>
                <span v-else>
                  <component :is="item.icon" class="shrink-0" />
                  <span>{{ item.name }}</span>
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </template>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
    <SidebarFooter class="items-center">
      <UnlockFeatures v-if="IS_BILLING_ENABLED ? !isVerified && isMember : false" />
      <ThemeSwitcher />
    </SidebarFooter>
  </Sidebar>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ChevronRightIcon } from 'lucide-vue-next';
import { type Component, computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import {
  type Profile,
  profileQueryOptions,
  useProfileStatus,
  type UserFeature,
} from '@/entities/profile';
import { IS_BAREMETAL_ENABLED, IS_BILLING_ENABLED } from '@/shared/config';
import { cn } from '@/shared/lib/misc';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/shared/ui/sidebar';
import ThemeSwitcher from '@/shared/ui/theme-switcher.vue';

import { SearchButton } from '../search-overlay';
import ApiIcon from './icons/Api.vue';
import BucketIcon from './icons/Buckets.vue';
import IaasIcon from './icons/IaaS.vue';
import ImagesIcon from './icons/Images.vue';
import NetworkIcon from './icons/Network.vue';
import SecurityIcon from './icons/Security.vue';
import StackletIcon from './icons/Stacklets.vue';
import UserscriptsIcon from './icons/Userscripts.vue';
import UnlockFeatures from './unlock-features.vue';

const route = useRoute();
const { data: profile } = useQuery(profileQueryOptions());
const { isVerified, isMember } = useProfileStatus(profile);
const { setOpenMobile } = useSidebar();

interface NavigationItem {
  name: string;
  icon: Component;
  tooltip?: string;
  routeName?: string;
  defaultOpen?: boolean;
  isVisible?: (profile: Profile | undefined) => boolean;
  children?: {
    name: string;
    routeName: string;
    isVisible?: (profile: Profile | undefined) => boolean;
  }[];
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

const navigationGroups = computed((): NavigationGroup[] => [
  {
    label: 'Manage',
    items: [
      {
        name: 'Compute',
        icon: StackletIcon,
        tooltip: 'Compute',
        defaultOpen: true,
        children: [
          {
            name: 'Stacklets',
            routeName: 'stacklets',
          },
          {
            name: 'Baremetal',
            routeName: 'baremetal',
            isVisible: () => IS_BAREMETAL_ENABLED,
          },
        ],
      },
      {
        name: 'Images',
        icon: ImagesIcon,
        tooltip: 'Images',
        children: [
          {
            name: 'Snapshots',
            routeName: 'images/snapshots',
          },
          {
            name: 'Backups',
            routeName: 'images/backups',
          },
          {
            name: 'Custom Images',
            routeName: 'images/custom',
          },
        ],
      },
      {
        name: 'Networking',
        icon: NetworkIcon,
        tooltip: 'Networking',
        isVisible: (profile: Profile | undefined) =>
          (['floating_ips', 'firewalls', 'domains', 'load_balancers', 'vpc'] as UserFeature[]).some(
            (feature) => profile?.features?.includes(feature),
          ),
        children: [
          {
            name: 'Floating IPs',
            routeName: 'networking/floating-ips',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('floating_ips') ?? false,
          },
          {
            name: 'Domains',
            routeName: 'networking/domains',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('domains') ?? false,
          },
          {
            name: 'Firewalls',
            routeName: 'networking/firewalls',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('firewalls') ?? false,
          },
          {
            name: 'Load Balancers',
            routeName: 'networking/loadbalancers',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('load_balancers') ?? false,
          },
          {
            name: 'VPC',
            routeName: 'networking/vpc',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('vpc') ?? false,
          },
        ],
      },
      {
        name: 'Storages',
        icon: BucketIcon,
        tooltip: 'Storages',
        isVisible: (profile: Profile | undefined) =>
          (profile?.features?.includes('volumes') || profile?.features?.includes('buckets')) ??
          false,
        children: [
          {
            name: 'Buckets',
            routeName: 'buckets',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('buckets') ?? false,
          },
          {
            name: 'Volumes',
            routeName: 'volumes',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('volumes') ?? false,
          },
        ],
      },
      {
        name: 'IaaS',
        icon: IaasIcon,
        tooltip: 'IaaS',
        isVisible: (profile: Profile | undefined) =>
          (profile?.features?.includes('container_registry') ||
            profile?.features?.includes('databases') ||
            profile?.features?.includes('kubernetes')) ??
          false,
        children: [
          {
            name: 'Databases',
            routeName: 'databases',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('databases') ?? false,
          },
          {
            name: 'Kubernetes',
            routeName: 'kubernetes',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('kubernetes') ?? false,
          },
          {
            name: 'Container Registry',
            routeName: 'registry',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('container_registry') ?? false,
          },
        ],
      },
    ],
  },
  {
    label: 'Resources',
    items: [
      {
        name: 'Security',
        icon: SecurityIcon,
        routeName: 'security',
      },
      {
        name: 'Userscripts',
        icon: UserscriptsIcon,
        routeName: 'userscripts',
      },
      {
        name: 'API',
        icon: ApiIcon,
        routeName: 'api',
      },
    ],
  },
]);

const isChildRouteActive = (children?: NavigationItem['children']) => {
  if (!children) return false;
  return children.some((child) => route.name === child.routeName);
};

const visibleNavigationGroups = computed(() =>
  navigationGroups.value.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => !item.isVisible || item.isVisible(profile.value))
      .map((item) => ({
        ...item,
        isOpen: item.defaultOpen || isChildRouteActive(item.children),
        children: item.children?.filter(
          (child) => !child.isVisible || child.isVisible(profile.value),
        ),
      }))
      .filter((item) => !item.children || item.children.length > 0),
  })),
);

watch(route, () => setOpenMobile(false));
</script>
